import {createSlice} from '@reduxjs/toolkit';
import {UnsplashResponse} from './model';

export interface MediaFile {
    title: any;
    name: string;
    file_uid: string;
    path: string;
    file_size: number;
    cdn_url: string;
    thumbnail_url: string;
}

interface Share {
    user_email: string;
    access_level: string;
}

export interface MediaFolder {
    uid: string;
    parent_uid: string | null;
    name: string;
    shares: Share[];
}

export enum MediaAnnotationSource {
    AZURE = 'AZURE',
    USER = 'USER',
}

export interface Caption {
    text: string;
    source_type: MediaAnnotationSource;
    score: number;
}

export interface Tag {
    text: string;
    source_type: MediaAnnotationSource;
}

export interface saved_images {
    [key: string]: {
        url: string;
        uid: string;
        height: number;
        width: number;
        size: number;
        type: string;
        created_at: number;
        parent_uid: string;
        title: string;
    };
}

export interface FileData {
    name: string;
    path: string;
    file_size: number;
    cdn_url: string;
    thumbnail_url: string;
    type: string;
    uploaded_by: string;
    created_at: string;
    client_ip: string;
    user_agent: string;
    uid: string;
    caption: Caption | null;
    tags: Tag[];
    width: number;
    height: number;
    saved_images: saved_images;
    folder?: MediaFolder;
}

interface ImageFile {
    url: string;
    org_uid: string;
    media_image_uid: string;
    media_file_uid: string;
}

interface DeletedMediaFile {
    file_uid: string;
    org_uid: string;
}

export interface BGSampleImageInfo {
    name: string;
    cdn_url: string;
    thumbnail_url: string;
    uid: string;
    theme?: string;
}

interface MediaReducer {
    fileData: FileData;
    folders: Array<MediaFolder>;
    files: Array<MediaFile>;
    tablePaginatedData: Array<MediaFile>;
    offset: number;
    pageCount: number;
    totalResults: number;
    searchResults: Array<MediaFile>;
    isSearchLoading: boolean;
    isUploadLoading: boolean;
    isListLoading: boolean;
    error: string | null;
    listError: string | null;
    fileError: string | null;
    uploadedFiles: Array<File>;
    uploadingFiles: Array<string>;
    fileUploadList: Array<any>;
    errorFiles: Array<{
        fileName: string;
        error: string;
    }>;
    uploadedFile: string;
    savingImageFile: boolean;
    savedImageFiles: ImageFile | null;
    errorSavingImageFile: boolean;
    deletingImageFile: boolean;
    deletedImageFile: ImageFile | null;
    errorDeletedImageFile: boolean;
    deletingMediaFile: boolean;
    deletedMediaFile: DeletedMediaFile | null;
    errorDeletingMediaFile: boolean;
    backgroundSampleFiles: Array<BGSampleImageInfo>;
    sampleFileError: string | null;
    unsplashPhotos: UnsplashResponse;
    errorUnsplashPhotos: string | null;
}

const DefaultState: MediaReducer = {
    fileData: {
        name: '',
        path: '',
        file_size: 0,
        cdn_url: '',
        thumbnail_url: '',
        type: '',
        uploaded_by: '',
        created_at: '',
        client_ip: '',
        user_agent: '',
        uid: '',
        caption: null,
        tags: [],
        width: 0,
        height: 0,
        saved_images: {},
    },
    folders: [],
    files: [],
    tablePaginatedData: [],
    offset: 0,
    pageCount: 0,
    searchResults: [],
    totalResults: 0,
    isSearchLoading: false,
    isUploadLoading: false,
    error: null,
    listError: null,
    fileError: null,
    isListLoading: false,
    uploadedFiles: [],
    uploadingFiles: [],
    fileUploadList: [],
    errorFiles: [],
    uploadedFile: '',
    savingImageFile: false,
    savedImageFiles: null,
    errorSavingImageFile: false,
    deletingImageFile: false,
    deletedImageFile: null,
    errorDeletedImageFile: false,
    deletingMediaFile: false,
    deletedMediaFile: null,
    errorDeletingMediaFile: false,
    backgroundSampleFiles: [],
    sampleFileError: null,
    unsplashPhotos: {
        results: [],
    },
    errorUnsplashPhotos: null,
};

const MediaFileSlice = createSlice({
    name: 'mediaFileUpload',
    initialState: DefaultState,
    reducers: {
        mediaFileBulkUpload: (state, action) => {
            const idx = state.fileUploadList.findIndex((item: any) => {
                return item.fileName === action.payload.fileName;
            });
            if (idx === -1) {
                state.fileUploadList.push({...action.payload});
            } else {
                state.fileUploadList[idx] = {...action.payload};
            }
        },
        mediaFileUploadInit: (state, action) => {
            state.isUploadLoading = true;
            state.uploadingFiles.push(action.payload.fileName);
        },
        mediaUploadSuccess: (state, action) => {
            state.uploadedFiles.push(action.payload.file);
            state.uploadedFile = action.payload.file;
            state.isUploadLoading = false;
            state.uploadingFiles = [];
        },
        mediaUploadError: (state, action) => {
            state.isUploadLoading = false;
            state.errorFiles.push({...action.payload});
            state.error = action.payload;
            state.uploadingFiles = [];
        },
        mediaListFilesInit: (state) => {
            state.isListLoading = true;
        },
        mediaSearchFilesInit: (state) => {
            state.isSearchLoading = true;
        },
        mediaListFilesSuccess: (state, action) => {
            const {files, folders, totalResults} = action.payload;
            state.folders = folders;
            state.files = files;
            state.isListLoading = false;
            state.totalResults = totalResults;
        },

        mediaAllPaginatedFiles: (state, action) => {
            const {tablePaginatedData, offset, pageCount} = action.payload;
            state.tablePaginatedData = tablePaginatedData;
            state.offset = offset;
            state.pageCount = pageCount;
        },

        mediaSearchFilesSuccess: (state, action) => {
            const {files, totalResults} = action.payload;
            state.searchResults = files;
            state.totalResults = totalResults;
            state.isSearchLoading = false;
        },

        mediaFileSuccess: (state, action) => {
            state.fileData = action.payload;
        },

        mediaFileClear: (state) => {
            state.fileData = DefaultState.fileData;
        },

        mediaFileListClear: (state) => {
            state.fileUploadList = DefaultState.fileUploadList;
        },

        mediaFileError: (state, action) => {
            state.fileError = action.payload;
        },

        mediaSampleFileError: (state, action) => {
            state.sampleFileError = action.payload;
        },

        mediaListFileError: (state, action) => {
            state.listError = action.payload;
            state.isListLoading = false;
        },
        mediaSearchFilesError: (state) => {
            state.isSearchLoading = false;
            state.searchResults = [];
        },

        mediaFileDeleteInit: (state) => {
            state.deletingMediaFile = true;
            state.deletedMediaFile = null;
            state.errorDeletingMediaFile = false;
        },
        mediaFileDeleteSuccess: (state, action) => {
            state.deletingMediaFile = false;
            state.deletedMediaFile = action.payload;
        },
        mediaFileDeleteFailed: (state, action) => {
            state.errorDeletingMediaFile = true;
        },
        mediaFileImageSaveInit: (state) => {
            state.savingImageFile = true;
            state.savedImageFiles = null;
            state.errorSavingImageFile = false;
        },
        mediaFileImageSaveSuccess: (state, action) => {
            state.savedImageFiles = action.payload;
        },
        mediaFileImageSaveFailed: (state, action) => {
            state.errorSavingImageFile = true;
        },
        mediaFileImageDeleteInit: (state) => {
            state.deletingImageFile = true;
            state.deletedImageFile = null;
            state.errorDeletedImageFile = false;
        },
        mediaFileImageDeleteSuccess: (state, action) => {
            state.deletedImageFile = action.payload;
        },
        mediaFileImageDeleteFailed: (state, action) => {
            state.errorDeletedImageFile = true;
        },
        mediaSampleFetchSuccess: (state, action) => {
            state.backgroundSampleFiles = action.payload;
        },
        mediaUnsplashSuccess: (state, action) => {
            state.unsplashPhotos = action.payload;
        },
        mediaUnsplashError: (state, action) => {
            state.errorUnsplashPhotos = state.error;
        },
    },
});
const {
    mediaFileUploadInit,
    mediaUploadSuccess,
    mediaUploadError,
    mediaListFilesSuccess,
    mediaSearchFilesSuccess,
    mediaSearchFilesInit,
    mediaSearchFilesError,
    mediaListFilesInit,
    mediaListFileError,
    mediaFileBulkUpload,
    mediaFileSuccess,
    mediaFileError,
    mediaFileDeleteInit,
    mediaFileDeleteSuccess,
    mediaFileDeleteFailed,
    mediaFileImageSaveInit,
    mediaFileImageSaveSuccess,
    mediaFileImageSaveFailed,
    mediaFileImageDeleteInit,
    mediaFileImageDeleteSuccess,
    mediaFileImageDeleteFailed,
    mediaFileClear,
    mediaSampleFetchSuccess,
    mediaSampleFileError,
    mediaUnsplashSuccess,
    mediaUnsplashError,
    mediaFileListClear,
    mediaAllPaginatedFiles,
} = MediaFileSlice.actions;

export {
    mediaFileUploadInit,
    mediaUploadSuccess,
    mediaUploadError,
    mediaSearchFilesSuccess,
    mediaSearchFilesInit,
    mediaSearchFilesError,
    mediaListFilesInit,
    mediaListFilesSuccess,
    mediaListFileError,
    mediaFileDeleteInit,
    mediaFileDeleteSuccess,
    mediaFileDeleteFailed,
    mediaFileImageSaveInit,
    mediaFileImageSaveSuccess,
    mediaFileImageSaveFailed,
    mediaFileImageDeleteInit,
    mediaFileImageDeleteSuccess,
    mediaFileImageDeleteFailed,
    mediaFileBulkUpload,
    mediaFileSuccess,
    mediaFileError,
    mediaFileClear,
    mediaSampleFetchSuccess,
    mediaSampleFileError,
    mediaUnsplashSuccess,
    mediaUnsplashError,
    mediaFileListClear,
    mediaAllPaginatedFiles,
};

export default MediaFileSlice.reducer;
